import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpGet, httpPost, httpPut } from "../../services/httpService";
import {
  getCompanyProductRulesByCompanyIdUrl,
  addCompanyProductRuleUrl,
  editCompanyProductRuleUrl,
  searchProductDrugUrl,
  getProductRuleAuditUrl,
  removeCompanyProductRuleUrl,
  getMonthlyConcessionUrl,
  getHistoricalConcessionUrl,
  addConcessionUrl,
  getTitanClaimsUrl,
  getPrescriptionImageUrl,
  getItemInfoUrl,
  saveClaimsUrl,
  getBlockedClaimsUrl, addEndorsementUrl, addUpdateExemptionUrl, getExemptionUrl, getDashboardStatsUrl,
  getGroupModeServicesUrl,
  getFp34SummaryUrl,
  getPharmacyPrivateServicesIncomeUrl,
  getPharmacyPrivateServicesIncomeByDateRangeUrl
} from "../../apiUrls/apiUrls";
import { logErrorWithPayload } from "../../services/logService";

const initialState = {
  isTitanTabLoading: false,
  companyProductRules: [],
  drugSearchResults: [],
  isDrugSearchLoading: false,
  productRuleAuditEntries: [],
  isAuditLoading: false,
  isRemovingProductRule: false,
  monthlyConcessions: [],
  isMonthlyConcessionLoading: false,
  historicalConcessions: [],
  isHistoricalConcessionLoading: false,
  isAddingConcession: false,
  titanClaims: [],
  isTitanClaimsLoading: false,
  blockedClaims: [],
  isBlockedClaimsLoading: false,
  groupModeServices: [],
  isServicesLoading: false,
  dashboardStats: null,
  isDashboardStatsLoading: false,
  privateServicesIncomeRange: {},
  privateServicesIncomeRangePending: 0,
  isPrivateServicesIncomeRangeLoading: false,
};

export const getCompanyProductRulesByCompanyIdData = createAsyncThunk(
  "titanTab/getCompanyProductRulesByCompanyIdData",
  async (companyId, thunkAPI) => {
    const urlToGet = getCompanyProductRulesByCompanyIdUrl(companyId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const addCompanyProductRuleData = createAsyncThunk(
  "titanTab/addCompanyProductRuleData",
  async (payload, thunkAPI) => {
    const url = addCompanyProductRuleUrl();
    const respData = await httpPost(url, payload);
    return respData;
  },
);

export const editCompanyProductRuleData = createAsyncThunk(
  "titanTab/editCompanyProductRuleData",
  async ({ prescribedItemId, ...body }, thunkAPI) => {
    const url = editCompanyProductRuleUrl(prescribedItemId);
    const respData = await httpPut(url, { ...body, prescribedItemId });
    return respData;
  },
);

export const searchProductDrugData = createAsyncThunk(
  "titanTab/searchProductDrugData",
  async (keyword, thunkAPI) => {
    const urlToGet = searchProductDrugUrl(keyword);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const removeCompanyProductRuleData = createAsyncThunk(
  "titanTab/removeCompanyProductRuleData",
  async ({ prescribedItemId, productRuleId }, thunkAPI) => {
    const urlToGet = removeCompanyProductRuleUrl(
      prescribedItemId,
      productRuleId,
    );
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getHistoricalConcessionData = createAsyncThunk(
  "titanTab/getHistoricalConcessionData",
  async (_, thunkAPI) => {
    const url = getHistoricalConcessionUrl();
    const respData = await httpGet(url);
    return respData;
  },
);

export const getMonthlyConcessionData = createAsyncThunk(
  "titanTab/getMonthlyConcessionData",
  async (concessionMonth, thunkAPI) => {
    const url = getMonthlyConcessionUrl(concessionMonth);
    const respData = await httpGet(url);
    return respData;
  },
);

export const addConcessionData = createAsyncThunk(
  "titanTab/addConcessionData",
  async (payload, thunkAPI) => {
    const url = addConcessionUrl();
    const respData = await httpPost(url, payload);
    return respData;
  },
);

export const getProductRuleAuditData = createAsyncThunk(
  "titanTab/getProductRuleAuditData",
  async ({ prescribedItemId, productRuleId }, thunkAPI) => {
    const urlToGet = getProductRuleAuditUrl(prescribedItemId, productRuleId);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getTitanClaimsData = createAsyncThunk(
  "titanTab/getTitanClaimsData",
  async (pharmacyId, thunkAPI) => {
    const url = getTitanClaimsUrl(pharmacyId);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getPrescriptionImageData = createAsyncThunk(
  "titanTab/getPrescriptionImageData",
  async ({ pharmacyId, incomingId }, thunkAPI) => {
    const url = getPrescriptionImageUrl(pharmacyId, incomingId);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getItemInfoData = createAsyncThunk(
  "titanTab/getItemInfoData",
  async ({ pharmacyId, prescribedItems }, thunkAPI) => {
    const url = getItemInfoUrl(pharmacyId);
    const respData = await httpPost(url, prescribedItems);
    return respData;
  },
);

export const getFp34SummaryData = createAsyncThunk(
  "titanTab/getFp34SummaryData",
  async ({ pharmacyId, cycleMonth }, thunkAPI) => {
    const url = getFp34SummaryUrl(pharmacyId, cycleMonth);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getPharmacyPrivateServicesIncomeData = createAsyncThunk(
  "titanTab/getPharmacyPrivateServicesIncomeData",
  async ({ companyId, pharmacyId, month }, thunkAPI) => {
    const url = getPharmacyPrivateServicesIncomeUrl(companyId, pharmacyId, month);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getPharmacyPrivateServicesIncomeByDateRangeData = createAsyncThunk(
  "titanTab/getPharmacyPrivateServicesIncomeByDateRangeData",
  async ({ companyId, pharmacyId, startDate, endDate }, thunkAPI) => {
    const url = getPharmacyPrivateServicesIncomeByDateRangeUrl(
      companyId,
      pharmacyId,
      startDate,
      endDate,
    );
    const respData = await httpGet(url);
    return respData;
  },
);

export const saveClaimsData = createAsyncThunk(
  "titanTab/saveClaimsData",
  async ({ pharmacyId, prescriptionIds }, thunkAPI) => {
    const url = saveClaimsUrl(pharmacyId);
    const respData = await httpPost(url, prescriptionIds);
    return respData;
  },
);

export const getBlockedClaimsData = createAsyncThunk(
  "titanTab/getBlockedClaimsData",
  async (pharmacyId, thunkAPI) => {
    const url = getBlockedClaimsUrl(pharmacyId);
    const respData = await httpGet(url);
    return respData;
  },
);

export const addEndorsementData = createAsyncThunk(
  "titanTab/addEndorsementData",
  async ({ pharmacyId, prescriptionItemId, additionalEndorses }, thunkAPI) => {
    const url = addEndorsementUrl(pharmacyId, prescriptionItemId);
    const respData = await httpPost(url, additionalEndorses);
    return respData;
  },
);
export const addUpdateExemptionData = createAsyncThunk(
  "titanTab/addUpdateExemptionData",
  async ({ pharmacyId, exemption }, thunkAPI) => {
    const url = addUpdateExemptionUrl(pharmacyId);
    const respData = await httpPost(url, exemption);
    return respData;
  },
);

export const getExemptionData = createAsyncThunk(
  "titanTab/getExemptionData",
  async ({ pharmacyId, prescriptionId }, thunkAPI) => {
    const url = getExemptionUrl(pharmacyId, prescriptionId);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getGroupModeServicesData = createAsyncThunk(
  "titanTab/getGroupModeServicesData",
  async (companyID, thunkAPI) => {
    const url = getGroupModeServicesUrl(companyID);
    const respData = await httpGet(url);
    return respData;
  },
);

export const getDashboardStatsData = createAsyncThunk(
  "titanTab/getDashboardStatsData",
  async (companyId, thunkAPI) => {
    const url = getDashboardStatsUrl(companyId);
    const respData = await httpGet(url);
    return respData;
  },
);

export const titanTabSlice = createSlice({
  name: "titanTab",
  initialState,
  reducers: {
    setIsTitanTabLoading: (state, action) => {
      state.isTitanTabLoading = action?.payload || false;
    },
    setCompanyProductRules: (state, action) => {
      state.companyProductRules = action?.payload || [];
    },
    resetTitanTabState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyProductRulesByCompanyIdData.pending, (state) => {
        state.isTitanTabLoading = true;
      })
      .addCase(
        getCompanyProductRulesByCompanyIdData.fulfilled,
        (state, action) => {
          state.isTitanTabLoading = false;
          state.companyProductRules = action?.payload || [];
        },
      )
      .addCase(
        getCompanyProductRulesByCompanyIdData.rejected,
        (state, action) => {
          logErrorWithPayload(action?.error);
          state.isTitanTabLoading = false;
          state.companyProductRules = [];
        },
      )
      .addCase(editCompanyProductRuleData.pending, (state) => {
        state.isTitanTabLoading = true;
      })
      .addCase(editCompanyProductRuleData.fulfilled, (state) => {
        state.isTitanTabLoading = false;
      })
      .addCase(editCompanyProductRuleData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isTitanTabLoading = false;
      })
      .addCase(addCompanyProductRuleData.pending, (state) => {
        state.isTitanTabLoading = true;
      })
      .addCase(addCompanyProductRuleData.fulfilled, (state) => {
        state.isTitanTabLoading = false;
      })
      .addCase(addCompanyProductRuleData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isTitanTabLoading = false;
      })
      .addCase(searchProductDrugData.pending, (state) => {
        state.isDrugSearchLoading = true;
      })
      .addCase(searchProductDrugData.fulfilled, (state, action) => {
        state.isDrugSearchLoading = false;
        state.drugSearchResults = action?.payload?.response || [];
      })
      .addCase(searchProductDrugData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isDrugSearchLoading = false;
        state.drugSearchResults = [];
      })
      .addCase(removeCompanyProductRuleData.pending, (state) => {
        state.isRemovingProductRule = true;
      })
      .addCase(removeCompanyProductRuleData.fulfilled, (state) => {
        state.isRemovingProductRule = false;
      })
      .addCase(removeCompanyProductRuleData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isRemovingProductRule = false;
      })
      .addCase(getHistoricalConcessionData.pending, (state) => {
        state.isHistoricalConcessionLoading = true;
        state.historicalConcessions = [];
      })
      .addCase(getHistoricalConcessionData.fulfilled, (state, action) => {
        state.isHistoricalConcessionLoading = false;
        state.historicalConcessions = action?.payload || [];
      })
      .addCase(getHistoricalConcessionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isHistoricalConcessionLoading = false;
        state.historicalConcessions = [];
      })
      .addCase(getMonthlyConcessionData.pending, (state) => {
        state.isMonthlyConcessionLoading = true;
        state.monthlyConcessions = [];
      })
      .addCase(getMonthlyConcessionData.fulfilled, (state, action) => {
        state.isMonthlyConcessionLoading = false;
        state.monthlyConcessions = action?.payload || [];
      })
      .addCase(getMonthlyConcessionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isMonthlyConcessionLoading = false;
        state.monthlyConcessions = [];
      })
      .addCase(addConcessionData.pending, (state) => {
        state.isAddingConcession = true;
      })
      .addCase(addConcessionData.fulfilled, (state) => {
        state.isAddingConcession = false;
      })
      .addCase(addConcessionData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isAddingConcession = false;
      })
      .addCase(getProductRuleAuditData.pending, (state) => {
        state.isAuditLoading = true;
        state.productRuleAuditEntries = [];
      })
      .addCase(getProductRuleAuditData.fulfilled, (state, action) => {
        state.isAuditLoading = false;
        state.productRuleAuditEntries = action?.payload?.auditEntries || [];
      })
      .addCase(getProductRuleAuditData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isAuditLoading = false;
        state.productRuleAuditEntries = [];
      })
      .addCase(getTitanClaimsData.pending, (state) => {
        state.isTitanClaimsLoading = true;
        state.titanClaims = [];
      })
      .addCase(getTitanClaimsData.fulfilled, (state, action) => {
        state.isTitanClaimsLoading = false;
        state.titanClaims = action?.payload?.data || [];
      })
      .addCase(getTitanClaimsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isTitanClaimsLoading = false;
        state.titanClaims = [];
      })
      .addCase(getBlockedClaimsData.pending, (state) => {
        state.isBlockedClaimsLoading = true;
        state.blockedClaims = [];
      })
      .addCase(getBlockedClaimsData.fulfilled, (state, action) => {
        state.isBlockedClaimsLoading = false;
        const payload = action?.payload;
        // get-blocked-claims returns Result<IEnumerable<BlockedClaimSummaryModel>>,
        // so the array is at payload.data (kept .response as a legacy fallback).
        state.blockedClaims = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : payload?.response || [];
      })
      .addCase(getBlockedClaimsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isBlockedClaimsLoading = false;
        state.blockedClaims = [];
      })
      .addCase(getDashboardStatsData.pending, (state) => {
        state.isDashboardStatsLoading = true;
      })
      .addCase(getDashboardStatsData.fulfilled, (state, action) => {
        state.isDashboardStatsLoading = false;
        const payload = action?.payload;
        state.dashboardStats = Array.isArray(payload) ? payload : payload?.response ?? [];
      })
      .addCase(getDashboardStatsData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isDashboardStatsLoading = false;
        state.dashboardStats = [];
      })

      .addCase(getPharmacyPrivateServicesIncomeByDateRangeData.pending, (state) => {
        state.privateServicesIncomeRangePending += 1;
        state.isPrivateServicesIncomeRangeLoading = true;
      })
      .addCase(getPharmacyPrivateServicesIncomeByDateRangeData.fulfilled, (state, action) => {
        state.privateServicesIncomeRangePending = Math.max(
          0,
          state.privateServicesIncomeRangePending - 1,
        );
        state.isPrivateServicesIncomeRangeLoading =
          state.privateServicesIncomeRangePending > 0;
        const payload = action?.payload;
        const data = payload?.response ?? payload?.data ?? payload;
        const pharmacyId = data?.pharmacyId ?? action?.meta?.arg?.pharmacyId;
        if (pharmacyId) {
          state.privateServicesIncomeRange[pharmacyId] = data ?? null;
        }
      })
      .addCase(getPharmacyPrivateServicesIncomeByDateRangeData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.privateServicesIncomeRangePending = Math.max(
          0,
          state.privateServicesIncomeRangePending - 1,
        );
        state.isPrivateServicesIncomeRangeLoading =
          state.privateServicesIncomeRangePending > 0;
        const pharmacyId = action?.meta?.arg?.pharmacyId;
        if (pharmacyId) {
          state.privateServicesIncomeRange[pharmacyId] = null;
        }
      })

       .addCase(getGroupModeServicesData.pending, (state) => {
        state.isServicesLoading = true;
        state.groupModeServices = [];
      })
      .addCase(getGroupModeServicesData.fulfilled, (state, action) => {
        state.isServicesLoading = false;
        state.groupModeServices = action?.payload;
      })
      .addCase(getGroupModeServicesData.rejected, (state, action) => {
        logErrorWithPayload(action?.error);
        state.isServicesLoading = false;
        state.groupModeServices = [];
      })
      ;
  },
});

export const {
  setIsTitanTabLoading,
  setCompanyProductRules,
  resetTitanTabState,
} = titanTabSlice.actions;

export default titanTabSlice.reducer;
